import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { ThunkApi } from '../../store'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { TransformationProfile, TransformationProfileInit, ListResult } from 'common/api/v1/types'
import { TransformationProfilesRequestParams } from '../../api/nm-types'
import { ApplicationException } from '../../api/ApplicationException'

export const listTransformationProfiles = createAsyncThunk<
  ListResult<TransformationProfile>,
  TransformationProfilesRequestParams,
  ThunkApi
>('transformationProfiles/listTransformationProfiles', async (params, { dispatch, extra: { api } }) => {
  try {
    return await api.transformationProfilesApi.listTransformationProfiles(params)
  } catch (error) {
    dispatch(enqueueErrorSnackbar({ error, operation: 'fetch transformation profiles' }))
    throw error
  }
})

export const getTransformationProfile = createAsyncThunk<TransformationProfile, string, ThunkApi>(
  'transformationProfiles/getTransformationProfile',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.transformationProfilesApi.getTransformationProfile(id)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'fetch transformation profile' }))
      throw error
    }
  },
)

export const createTransformationProfile = createAsyncThunk<TransformationProfile, TransformationProfileInit, ThunkApi>(
  'transformationProfiles/createTransformationProfile',
  async (data, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      const result = await api.transformationProfilesApi.createTransformationProfile(data)
      navigate()(routes.transformationProfiles())
      dispatch(enqueueSuccessSnackbar(`Transformation profile "${result.name}" created successfully`))
      return result
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'create transformation profile' }))
      if (error instanceof ApplicationException) return rejectWithValue(error.errorInfo)
      throw error
    }
  },
)

export const updateTransformationProfile = createAsyncThunk<
  TransformationProfile,
  { id: string; data: TransformationProfileInit },
  ThunkApi
>(
  'transformationProfiles/updateTransformationProfile',
  async ({ id, data }, { dispatch, rejectWithValue, extra: { api } }) => {
    try {
      const result = await api.transformationProfilesApi.updateTransformationProfile(id, data)
      dispatch(enqueueSuccessSnackbar(`Transformation profile "${result.name}" updated successfully`))
      return result
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'update transformation profile' }))
      if (error instanceof ApplicationException) return rejectWithValue(error.errorInfo)
      throw error
    }
  },
)

export const deleteTransformationProfile = createAsyncThunk<{ id: string }, string, ThunkApi>(
  'transformationProfiles/deleteTransformationProfile',
  async (id, { dispatch, extra: { api } }) => {
    try {
      await api.transformationProfilesApi.deleteTransformationProfile(id)
      dispatch(enqueueSuccessSnackbar('Transformation profile deleted successfully'))
      return { id }
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete transformation profile' }))
      throw error
    }
  },
)

export const clearTransformationProfileErrors = createAction('transformationProfiles/clearTransformationProfileErrors')
